// src/screens/ShotDataScreen.jsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination,
  Alert,
  Card,
} from "react-bootstrap";
import {
    FaEdit,
    FaTrash,
    FaPlus,
    FaSortUp,
    FaSortDown,
    FaChevronDown,
    FaChevronUp,
    FaDownload,
    FaCheck,
    FaTimes,
    FaQuestionCircle,
} from "react-icons/fa";
import {
  useGetGameDataQuery,
  useUpdateGameDataMutation,
  useDeleteGameDataMutation,
} from "../slices/gameDataApiSlice";
import {
  useGetShotsByGameIdQuery,
  useCreateShotDataMutation,
  useUpdateShotDataMutation,
  useDeleteShotDataMutation,
} from "../slices/shotDataApiSlice";
import ConfirmDeletion from "../components/ConfirmDeletion";
import Loader from "../components/Loader";
import Message from "../components/Message";
import AddGameModal from "../components/Game/AddGameModal";
import EditGameModal from "../components/Game/EditGameModal";
import AddShotModal from "../components/Shots/AddShotModal";
import EditShotModal from "../components/Shots/EditShotModal";
import ScreenTabs from "../components/ScreenTabs";
import {
  calculateShotExpectedScoreNew,
  calculateVerticalWind,
} from "../utils/expectedScore";
import { toast } from "react-toastify";

const MANAGE_DATA_TABS = [
  { label: "Game Data", to: "/ShotDataScreen" },
  { label: "Squad Data", to: "/SquadDataScreen" },
];

// ─── Helpers for exp‐score clustering ────────────────────────────────────────
function clusterByAttack(shots) {
  const clusters = [];
  for (let shot of shots) {
    if (shot.newAttack === "Yes") {
      clusters.push([shot]);
    } else {
      if (clusters.length === 0) {
        // start first cluster even if first shot is "No"
        clusters.push([shot]);
      } else {
        clusters[clusters.length - 1].push(shot);
      }
    }
  }
  return clusters;
}

function sumMaxExpectedScore(shots) {
  return clusterByAttack(shots)
    .map((cluster) =>
      cluster.reduce((mx, s) => Math.max(mx, s.expectedScore), 0),
    )
    .reduce((sum, mx) => sum + mx, 0);
}

// ─── Sub‐component for each game row ─────────────────────────────────────────
function GameRow({
  game,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  editButtonRef,
}) {
  const gameType = game?.GameType || "Mens";
  let { data: rowShots = [] } = useGetShotsByGameIdQuery({ gameId: game._id });
  rowShots = rowShots?.shotData || [];

  const outcomeValue = useCallback((v) =>
    v === 'Goal'    ? 3 :
    v === '2 Point' ? 2 :
    v === 'Point'   ? 1 : 0
  , [])

  const summarize = (team) => {
    const shots = rowShots.filter(s => s.teamShot === team)
    const exp   = sumMaxExpectedScore(shots).toFixed(1)
    const goals = shots.filter(s => s.shotOutcome === 'Goal').length
    const two2  = shots.filter(s => s.shotOutcome === '2 Point').length
    const ones  = shots.filter(s => s.shotOutcome === 'Point').length
    const pts   = shots.reduce((sum, s) => sum + outcomeValue(s.shotOutcome), 0)
    return { exp, goals, two2, ones, pts }
  };

  const home = summarize(game.HomeTeam);
  const away = summarize(game.AwayTeam);
  const scoreSummary =
    `[${home.exp}] ${home.goals}-${home.two2}-${home.ones} (${home.pts})` +
    ` : (${away.pts}) ${away.goals}-${away.two2}-${away.ones} [${away.exp}]`;

  return (
    <tr
      onClick={() => onSelect(game)}
      className={isSelected ? "table-active" : ""}
      style={{ cursor: "pointer" }}
    >
      <td>{game.GameDate}</td>
      <td>{game.Competition}</td>
      <td>{gameType}</td>
      <td>{game.HomeTeam}</td>
      <td>{scoreSummary}</td>
      <td>{game.AwayTeam}</td>
      <td>
        <Button
          ref={editButtonRef}
          size="sm"
          variant="warning"
          className="text-dark app-table-edit-btn"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(game);
          }}
        >
          <FaEdit />
        </Button>
      </td>
      <td>
        <Button
          size="sm"
          variant="danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(game);
          }}
        >
          <FaTrash />
        </Button>
      </td>
    </tr>
  );
}

// ─── Main screen component ───────────────────────────────────────────────────
const ShotDataScreen = () => {
  const pageHeaderRef = useRef(null);
  const gamesSectionRef = useRef(null);
  const noGameDataRef = useRef(null);
  const noSelectedGameRef = useRef(null);
  const addGameButtonRef = useRef(null);
  const firstEditGameButtonRef = useRef(null);
  const shotDetailsRef = useRef(null);
  const gameDetailsRef = useRef(null);

  // helper to format seconds into MM:SS
  function formatTime(sec) {
    const total = Number(sec) || 0;

    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    return [h, m, s]
      .map(x => String(x).padStart(2, "0"))
      .join(":");
  } 

  function deriveDisplayedGameTimeSeconds(shot, game) {
    return Number(shot?.gameTimeSeconds) || 0;
  }

  function deriveEnteredVideoTimeSeconds(shot, game) {
    const storedGameTime = Number(shot?.gameTimeSeconds);
    if (!Number.isFinite(storedGameTime)) return 0;

    const firstHalfStart = Number(game?.FirstHalfVideoTime);
    const secondHalfStart = Number(game?.SecondHalfVideoTime);

    if (Number(shot?.gameHalf) === 1 && Number.isFinite(firstHalfStart)) {
      return storedGameTime + firstHalfStart;
    }
    if (Number(shot?.gameHalf) === 2 && Number.isFinite(secondHalfStart)) {
      return storedGameTime + secondHalfStart;
    }
    if (Number(shot?.gameHalf) === 3 && Number.isFinite(secondHalfStart)) {
      return storedGameTime + secondHalfStart;
    }

    return storedGameTime;
  }

  // — games table state & hooks —
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("GameDate");
  const [sortOrder, setSortOrder] = useState("desc");

  const {
    data,
    isLoading,
    error,
    refetch: refetchGames,
  } = useGetGameDataQuery(
    { page, limit, search, sort: sortKey, order: sortOrder },
    { refetchOnMountOrArgChange: true },
  );

  const [updateGame] = useUpdateGameDataMutation();
  const [deleteGame] = useDeleteGameDataMutation();

  // — selected game & its CRUD modals & confirmation —
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // sorting for game details
  const [summarySortKey, setSummarySortKey] = useState("shotType");
  const [summarySortOrder, setSummarySortOrder] = useState("asc");

  const handleSummarySort = (key) => {
    if (summarySortKey === key) {
      setSummarySortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSummarySortKey(key);
      setSummarySortOrder("asc");
    }
  };

  // - shot detail states
  const [shotDetailSortField, setShotDetailSortField] =
    useState("gameTimeSeconds");
  const [shotDetailSortOrder, setShotDetailSortOrder] = useState(1);
  const [shotDetailSearch, setShotDetailSearch] = useState("");

  // — shots list & CRUD for selected game —
  const {
    data: shotsDataRes = {},
    isLoading: shotsLoading,
    error: shotsError,
    refetch: refetchShots,
  } = useGetShotsByGameIdQuery(
    {
      gameId: selectedEntry?._id,
      limit: 0,
      sort: shotDetailSortField === "gameTimeSeconds" ? "createdAt" : shotDetailSortField,
      order: shotDetailSortOrder,
      search: shotDetailSearch,
    },
    {
      skip: !selectedEntry,
      refetchOnMountOrArgChange: true,
    },
  );
  //extract fields from API slice
  const rawShotsData = useMemo(() => shotsDataRes?.shotData || [], [shotsDataRes]);
  const totalShots = shotsDataRes.totalShots || 0;
  const { data: summaryShotsRes = {} } = useGetShotsByGameIdQuery(
    {
      gameId: selectedEntry?._id,
      limit: 0,
    },
    {
      skip: !selectedEntry,
      refetchOnMountOrArgChange: true,
    },
  );
  const summaryShotsData = useMemo(
    () => summaryShotsRes?.shotData || [],
    [summaryShotsRes],
  );
  const shotsData = useMemo(() => {
    const dataToSort = [...rawShotsData];
    const direction = shotDetailSortOrder === -1 ? -1 : 1;

    return dataToSort.sort((a, b) => {
      if (shotDetailSortField === "gameTimeSeconds") {
        const halfCompare =
          ((Number(a?.gameHalf) || 0) - (Number(b?.gameHalf) || 0)) * direction;
        if (halfCompare !== 0) return halfCompare;
        return (
          ((Number(a?.gameTimeSeconds) || 0) - (Number(b?.gameTimeSeconds) || 0)) *
          direction
        );
      }

      const aValue = a?.[shotDetailSortField];
      const bValue = b?.[shotDetailSortField];

      if (typeof aValue === "number" || typeof bValue === "number") {
        return ((Number(aValue) || 0) - (Number(bValue) || 0)) * direction;
      }

      return (
        String(aValue ?? "").localeCompare(String(bValue ?? "")) * direction
      );
    });
  }, [rawShotsData, shotDetailSortField, shotDetailSortOrder]);

  const latestShotByGameTime = useMemo(() => {
    if (!summaryShotsData.length) return null;
    return [...summaryShotsData].sort(
      (a, b) =>
        (Number(b?.gameHalf) || 0) - (Number(a?.gameHalf) || 0) ||
        (Number(b?.gameTimeSeconds) || 0) - (Number(a?.gameTimeSeconds) || 0),
    )[0];
  }, [summaryShotsData]);

  const [createShot] = useCreateShotDataMutation();
  const [updateShot] = useUpdateShotDataMutation();
  const [deleteShot] = useDeleteShotDataMutation();

  const [selectedShot, setSelectedShot] = useState(null);
  const [selectedShotToDelete, setSelectedShotToDelete] = useState(null);
  const [showAddShotModal, setShowAddShotModal] = useState(false);
  const [showEditShotModal, setShowEditShotModal] = useState(false);
  const [showShotConfirm, setShowShotConfirm] = useState(false);
  const [previousSelectedHalf, setPreviousSelectedHalf] = useState("1");
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const [tutorialSelectedGame, setTutorialSelectedGame] = useState(false);

  // — multi-delete state for shots —
  const [multiDeleteMode, setMultiDeleteMode] = useState(false);
  const [selectedShots, setSelectedShots] = useState([]);
  const [showMultiDeleteConfirm, setShowMultiDeleteConfirm] = useState(false);

  // — toggles for collapse panels —
  const [openGameDetails, setOpenGameDetails] = useState(false);
  const [openShotDetails, setOpenShotDetails] = useState(true);

  // — filtering & pagination within game details —
  const [detailSearch, setDetailSearch] = useState("");

  const hasTutorialGamesAvailable = Boolean(selectedEntry) || Boolean(data?.entries?.length);

  const tutorialSteps = useMemo(() => {
    const baseSteps = [
      {
        key: "overview",
        title: "Page Overview",
        body: "Start here. This page manages games, lets you drill into one match, and opens the add or edit flows for game and shot data.",
        ref: pageHeaderRef,
      },
      {
        key: "games",
        title: "Games List",
        body: "Use search, paging, and row selection here. Clicking a game drives the details and shot data shown below.",
        ref: gamesSectionRef,
      },
      {
        key: "addGame",
        title: "Add Game",
        body: "This opens the game-entry modal. It is the first step in the workflow because shot data depends on a game existing first.",
        ref: addGameButtonRef,
      },
    ];

    if (!hasTutorialGamesAvailable) {
      return [
        ...baseSteps,
        {
          key: "noGameData",
          title: "No Game Data Yet",
          body: "There are no games in the system yet. For a new user, this is expected until the first match is added or imported.",
          ref: noGameDataRef,
        },
        {
          key: "noSelectedGame",
          title: "What Unlocks Next",
          body: "Once you add a game, this page unlocks shot entry, CSV export, and the game summary panels below. This empty state is the handoff that tells you to add or select a game first.",
          ref: noSelectedGameRef,
        },
      ];
    }

    return [
      ...baseSteps,
      {
        key: "editGame",
        title: "Edit Game",
        body: "Use this button on an existing row to correct game metadata like teams, competition, or the half video-time anchors used in shot calculations.",
        ref: firstEditGameButtonRef,
      },
      {
        key: "shotDetail",
        title: "Shot Detail",
        body: "After a game is selected, this becomes the main working area. Search, sort, add shots, export CSV, and edit or delete individual shots here.",
        ref: shotDetailsRef,
      },
      {
        key: "gameDetails",
        title: "Game Details",
        body: "This summary panel helps review the selected match by team, shot type, and outcome once the game has been selected.",
        ref: gameDetailsRef,
      },
    ];
  }, [hasTutorialGamesAvailable]);

  const updateTutorialRect = useCallback(() => {
    if (!showTutorial) {
      setTutorialRect(null);
      return;
    }

    const currentStep = tutorialSteps[tutorialStepIndex];
    const node = currentStep?.ref?.current;
    if (!node) {
      setTutorialRect(null);
      return;
    }

    const rect = node.getBoundingClientRect();
    setTutorialRect({
      top: Math.max(8, rect.top - 8),
      left: Math.max(8, rect.left - 8),
      width: rect.width + 16,
      height: rect.height + 16,
    });
  }, [showTutorial, tutorialStepIndex, tutorialSteps]);

  useEffect(() => {
    if (!showTutorial) return undefined;

    if (tutorialStepIndex > tutorialSteps.length - 1) {
      setTutorialStepIndex(Math.max(0, tutorialSteps.length - 1));
      return undefined;
    }

    const currentStep = tutorialSteps[tutorialStepIndex];
    if (
      ["editGame", "shotDetail", "gameDetails"].includes(currentStep?.key) &&
      !selectedEntry &&
      data?.entries?.length
    ) {
      setSelectedEntry(data.entries[0]);
      setTutorialSelectedGame(true);
    }
    if (currentStep?.key === "shotDetail") {
      setOpenShotDetails(true);
    }
    if (currentStep?.key === "gameDetails") {
      setOpenGameDetails(true);
    }
    currentStep?.ref?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    const refreshRect = () => updateTutorialRect();
    refreshRect();
    const timeoutId = window.setTimeout(refreshRect, currentStep?.key === "gameDetails" ? 420 : 220);
    const secondTimeoutId = window.setTimeout(() => {
      currentStep?.ref?.current?.scrollIntoView({
        behavior: "smooth",
        block: currentStep?.key === "gameDetails" ? "start" : "center",
      });
      refreshRect();
    }, currentStep?.key === "gameDetails" ? 720 : 320);
    const intervalId = window.setInterval(refreshRect, 70);
    const stopIntervalId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, currentStep?.key === "gameDetails" ? 1100 : 800);

    window.addEventListener("resize", refreshRect);
    window.addEventListener("scroll", refreshRect, true);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(secondTimeoutId);
      window.clearTimeout(stopIntervalId);
      window.clearInterval(intervalId);
      window.removeEventListener("resize", refreshRect);
      window.removeEventListener("scroll", refreshRect, true);
    };
  }, [
    showTutorial,
    tutorialStepIndex,
    tutorialSteps,
    updateTutorialRect,
    selectedEntry,
    data,
  ]);

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setTutorialSelectedGame(false);
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialRect(null);
    if (tutorialSelectedGame) {
      setSelectedEntry(null);
      setOpenGameDetails(false);
      setOpenShotDetails(true);
    }
    setTutorialSelectedGame(false);
  };

  const nextTutorialStep = () => {
    if (tutorialStepIndex >= tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStepIndex((prev) => prev + 1);
  };

  const prevTutorialStep = () => {
    setTutorialStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const getTutorialCardStyle = () => {
    const cardWidth = Math.min(360, window.innerWidth - 32);

    const gap = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const placeBelow =
      tutorialRect.top + tutorialRect.height + 220 + gap < viewportHeight;
    const top = placeBelow
      ? tutorialRect.top + tutorialRect.height + gap
      : Math.max(16, tutorialRect.top - 220 - gap);

    const preferredLeft = tutorialRect.left;
    const maxLeft = Math.max(16, viewportWidth - cardWidth - 16);
    const left = Math.min(Math.max(16, preferredLeft), maxLeft);

    return {
      position: "fixed",
      top,
      left,
      width: cardWidth,
      zIndex: 1042,
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
      transition: "top 160ms ease, left 160ms ease",
    };
  };

  // ── Game handlers ───────────────────────────────────────────────────────────
  const handleSort = (key) => {
    if (key === sortKey) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const handleUpdate = async (upd) => {
    const { recalculateShotsForWindChange = false, ...gameUpdate } = upd;
    let gameUpdated = false;

    try {
      const updatedGame = await updateGame(gameUpdate).unwrap();
      gameUpdated = true;

      if (recalculateShotsForWindChange && summaryShotsData.length) {
        await Promise.all(
          summaryShotsData.map((shot) => {
            const distance = Number(shot.distance) || 0;
            const angle = Number(shot.angle) || 0;


            // Determine if direction of play with +1 if teamShot is homeTeam, -1 if awayTeam for first half (or third half)  and opposite for second half. This assumes data entry from the perspective of the home team attacking left-to-right, which is how the video footage is usually recorded.
            const dirShot = (shot.teamShot === updatedGame.HomeTeam ? 1 : -1) * (shot.gameHalf.toString() === "2" ? -1 : 1);
            const vertWind = dirShot * updatedGame.WindStrength * Math.cos((updatedGame.WindAngle * Math.PI) / 180);


            // console.log("Recalculating shot", shot._id,  "half", shot.gameHalf, "vertWind", vertWind);

            // const vertWind = calculateVerticalWind(
            //   updatedGame.WindStrength,
            //   updatedGame.WindAngle,
            //   angle,
            // );
            const expectedScore = calculateShotExpectedScoreNew(
              distance,
              angle,
              shot.shotPressure,
              shot.setPlay,
              shot.shotMethod,
              shot.side,
              shot.shotType,
              vertWind,
              updatedGame.GameType || "Mens",
            );

            return updateShot({
              id: shot._id,
              gameDate: new Date(updatedGame.GameDate).getTime(),
              homeTeam: updatedGame.HomeTeam,
              awayTeam: updatedGame.AwayTeam,
              gameHalf: Number(shot.gameHalf),
              gameTimeSeconds: Number(shot.gameTimeSeconds),
              teamShot: shot.teamShot,
              playerName: shot.playerName,
              setPlay: shot.setPlay,
              shotOutcome: shot.shotOutcome,
              shotPressure: shot.shotPressure,
              shotType: shot.shotType,
              shotMethod: shot.shotMethod,
              distance,
              angle,
              side: shot.side,
              newAttack: shot.newAttack,
              expectedScore,
              gameId: updatedGame._id,
            }).unwrap();
          }),
        );
      }

      toast.success(
        recalculateShotsForWindChange && summaryShotsData.length
          ? `Game updated and recalculated ${summaryShotsData.length} shots`
          : "Game updated",
      );
      setShowEditModal(false);
      setSelectedEntry(updatedGame);
      refetchGames();
      refetchShots();
      return true;
    } catch (error) {
      toast.error(
        recalculateShotsForWindChange && gameUpdated
          ? "Game was updated, but shot recalculation failed"
          : "Failed to update game",
      );
      return false;
    }
  };

  const handleDeleteGame = (entry) => {
    if (!entry || !entry._id) return;
    deleteGame(entry._id)
      .unwrap()
      .then(() => {
        toast.success("Game deleted");
        setShowConfirm(false);
        setSelectedEntry(null);
        refetchGames();
      })
      .catch(() => toast.error("Failed to delete game"));
  };

  // ── Shot handlers ───────────────────────────────────────────────────────────
  const handleAddShot = async (shotData) => {
    try {
      await createShot({
        ...shotData,
        gameDate: new Date(selectedEntry.GameDate).getTime(),
        homeTeam: selectedEntry.HomeTeam,
        awayTeam: selectedEntry.AwayTeam,
        gameId: selectedEntry._id,
      }).unwrap();
      toast.success("Shot added", {
        autoClose: 1200,
        hideProgressBar: true,
      });
      refetchShots();
    } catch (err) {
      toast.error("Failed to add shot");
      throw err;
    }
  };

  const handleEditShot = (upd) => {
    updateShot(upd)
      .unwrap()
      .then(() => {
        toast.success("Shot updated");
        setShowEditShotModal(false);
        setSelectedShot(null);
        refetchShots();
      })
      .catch(() => toast.error("Failed to update shot"));
  };

  const handleDeleteShot = () => {
    if (!selectedShotToDelete?._id) return;
    deleteShot(selectedShotToDelete._id)
      .unwrap()
      .then(() => {
        toast.success("Shot deleted");
        setShowShotConfirm(false);
        setSelectedShotToDelete(null);
        refetchShots();
      })
      .catch(() => toast.error("Failed to delete shot"));
  };

  // ── Multi-delete helpers ────────────────────────────────────────────────────
  const toggleMultiDelete = () => {
    setMultiDeleteMode((m) => {
      if (m) setSelectedShots([]);
      return !m;
    });
  };

  const handleSelectShot = (id, checked) => {
    setSelectedShots((s) => (checked ? [...s, id] : s.filter((x) => x !== id)));
  };

  const handleDeleteSelected = () => {
    Promise.all(selectedShots.map((id) => deleteShot(id).unwrap()))
      .then(() => {
        toast.success(`Deleted ${selectedShots.length} shots`);
        setShowMultiDeleteConfirm(false);
        setMultiDeleteMode(false);
        setSelectedShots([]);
        refetchShots();
      })
      .catch(() => toast.error("Failed to delete selected shots"));
  };

  // ── Export CSV ──────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!selectedEntry || shotsData.length === 0) return;

    const columns = [
      ["GameDate", "GameDate"],
      ["ThrowInTime", "ThrowInTime"],
      ["Competition", "Competition"],
      ["GameType", "GameType"],
      ["HomeTeam", "HomeTeam"],
      ["AwayTeam", "AwayTeam"],
      ["HomeTeamColor", "HomeTeamColor"],
      ["AwayTeamColor", "AwayTeamColor"],
      ["WindStrength", "WindStrength"],
      ["WindAngle", "WindAngle"],
      ["FirstHalfVideoTime", "FirstHalfVideoTime"],
      ["SecondHalfVideoTime", "SecondHalfVideoTime"],
      ["Team", "teamShot"],
      ["PlayerName", "playerName"],
      ["GameHalf", "gameHalf"],
      ["GameTimeSeconds", "gameTimeSeconds"],
      ["ShotPressure", "shotPressure"],
      ["ShotType", "shotType"],
      ["SetPlay", "setPlay"],
      ["ShotMethod", "shotMethod"],
      ["Distance", "distance"],
      ["Angle", "angle"],
      ["Side", "side"],
      ["ShotOutcome", "shotOutcome"],
      ["ExpectedScore", "expectedScore"],
      ["NewAttack", "newAttack"],
    ];

    const header = columns.map((c) => c[0]).join(",");
    const rows = shotsData.map((s) =>
      columns
        .map(([_, key]) => {
          const val =
            key === "GameType"
              ? selectedEntry.GameType || "Mens"
              : selectedEntry[key] !== undefined
                ? selectedEntry[key]
                : s[key];
          return `"${String(val ?? "").replace(/"/g, '""')}"`;
        })
        .join(","),
    );

    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `shots_${
      new Date(selectedEntry.GameDate).toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── New: group shots by (type, outcome) per team ──────────────────────────
  const summaryByTeam = useMemo(() => {
    if (!selectedEntry || !summaryShotsData.length) return {};

    const map = summaryShotsData.reduce((acc, shot) => {
      const { teamShot, shotType, shotOutcome } = shot;
      if (!acc[teamShot]) acc[teamShot] = {};
      const key = `${shotType}:::${shotOutcome}`;
      if (!acc[teamShot][key]) {
        acc[teamShot][key] = { shotType, shotOutcome, num: 0 };
      }
      acc[teamShot][key].num++;
      return acc;
    }, {});

    return Object.fromEntries(
      Object.entries(map).map(([team, buckets]) => [
        team,
        Object.values(buckets),
      ]),
    );
  }, [summaryShotsData, selectedEntry]);

  const shotDetailsColumns = {
    Type: "shotType",
    Half: "gameHalf",
    "Game Time": "gameTimeSeconds",
    Team: "teamShot",
    Player: "playerName",
    Play: "setPlay",
    Pressure: "shotPressure",
    Outcome: "shotOutcome",
    "xPoints": "expectedScore",
    "New Attack": "newAttack",
  };

  const handleShotDetailSort = (field) => {
    if (field === shotDetailSortField) {
      setShotDetailSortOrder(shotDetailSortOrder * -1);
    } else {
      setShotDetailSortOrder(1);
      setShotDetailSortField(field);
    }
  };

  const stickyStyle = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#f59e0b",
    color: "#111827",
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
    <Container className="pt-4 app-section-page">
      <title>Game Data | ShotArc</title>
      <div>
        <ScreenTabs items={MANAGE_DATA_TABS} />
      </div>
      {/* ——— Games Table ——— */}
      <Row className="app-page-title-row align-items-center" ref={pageHeaderRef}>
        <Col>
          <h2 className="app-page-title">Games</h2>
        </Col>
        <Col className="text-end">
          <Button
            variant="outline-light"
            size="sm"
            className="me-2 d-inline-flex align-items-center gap-2"
            onClick={startTutorial}
          >
            <FaQuestionCircle /> Help Tour
          </Button>
          <Button
            ref={addGameButtonRef}
            variant="warning"
            className="text-dark fw-semibold"
            onClick={() => setShowAddModal(true)}
          >
            <FaPlus /> Add Game
          </Button>
        </Col>
      </Row>

      <div className="bg-dark border border-warning rounded p-3 mb-4" ref={gamesSectionRef}>
      <Row className="mb-3 align-items-end">
        <Col md={4}>
          <Form.Label>Search</Form.Label>
          <Form.Control
            placeholder="Search by Team or Competition"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Entries per page</Form.Label>
          <Form.Select
            value={limit}
            onChange={(e) => {
              setLimit(+e.target.value);
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={5} className="text-end">
          {data?.total > 0 && (
            <span className="text-white">
              Showing {(page - 1) * limit + 1}–
              {Math.min(page * limit, data.total)} of {data.total}
            </span>
          )}
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error.error}</Message>
      ) : data.entries.length === 0 ? (
        <Alert className="app-empty-state mb-0" ref={noGameDataRef}>No game data found.</Alert>
      ) : (
        <>
          <Table striped bordered hover responsive className="app-table">
            <thead className="table-dark">
              <tr>
                <th onClick={() => handleSort("GameDate")}>
                  Date{" "}
                  {sortKey === "GameDate" ? (
                    sortOrder === "asc" ? (
                      <FaSortUp />
                    ) : (
                      <FaSortDown />
                    )
                  ) : null}
                </th>
                <th>Competition</th>
                <th>Type</th>
                <th>Home</th>
                <th>Score</th>
                <th>Away</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {data.entries.map((g) => (
                <GameRow
                  key={g._id}
                  game={g}
                  isSelected={selectedEntry?._id === g._id}
                  editButtonRef={
                    data.entries[0]?._id === g._id ? firstEditGameButtonRef : undefined
                  }
                  onSelect={(entry) => setSelectedEntry(entry)}
                  onEdit={(entry) => {
                    setSelectedEntry(entry);
                    setShowEditModal(true);
                  }}
                  onDelete={(entry) => {
                    setSelectedEntry(entry);
                    setShowConfirm(true);
                  }}
                />
              ))}
            </tbody>
          </Table>

          {/* games pagination */}
          {data.pages > 1 && (
            <Pagination className="justify-content-center my-3 app-pagination">
              <Pagination.Prev
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page === 1}
              />
              {[...Array(data.pages)].map((_, i) => (
                <Pagination.Item
                  key={i}
                  active={i + 1 === page}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setPage((p) => Math.min(p + 1, data.pages))}
                disabled={page === data.pages}
              />
            </Pagination>
          )}
        </>
      )}
      </div>

      <Card className="border-warning mt-5" ref={shotDetailsRef}>
        <Card.Header
          className="bg-warning text-white d-flex justify-content-between align-items-center py-2"
          onClick={() => setOpenShotDetails((o) => !o)}
          style={{ cursor: "pointer" }}
        >
          <strong>Shot Detail</strong>
          <div className="d-flex align-items-center">
            {selectedEntry && (
              <>
                {!multiDeleteMode ? (
                  <Button
                    size="sm"
                    variant="danger"
                    className="me-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMultiDelete();
                    }}
                  >
                    Delete Many
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="danger"
                      className="me-2"
                      disabled={!selectedShots.length}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMultiDeleteConfirm(true);
                      }}
                    >
                      Delete Selected ({selectedShots.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      className="me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMultiDelete();
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="light"
                  className="me-2"
                  disabled={!shotsData.length}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportCSV();
                  }}
                >
                  <FaDownload /> Export CSV
                </Button>
              </>
            )}
            {openShotDetails ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </Card.Header>
        {openShotDetails && (
          <Card.Body className="bg-dark text-info">
            {selectedEntry && 
            <Row className="d-flex justify-content-between align-items-center my-3">
              <Col
                className="d-flex justify-content-around align-items-center"
                md={3}
              >
                <Form.Label>Search:</Form.Label>
                <Form.Control
                  size={"sm"}
                  type="text"
                  placeholder="Shot Type or Player"
                  value={shotDetailSearch}
                  onChange={(e) => {
                    setShotDetailSearch(e.target.value);
                  }}
                  className="mx-2"
                />
              </Col>
              <Col className="text-end" md={9}>
                <Button
                  variant="warning"
                  className="text-dark fw-semibold px-3 py-2"
                  onClick={() => setShowAddShotModal(true)}
                >
                  <FaPlus /> Add Shot
                </Button>
              </Col>
            </Row>
            }
            {!selectedEntry ? (
              <Alert className="app-empty-state mb-3 text-center" ref={noSelectedGameRef}>No game selected</Alert>
            ) : shotsLoading ? (
              <Alert className="app-empty-state mb-3 text-center">Loading shots...</Alert>
            ) : shotsError ? (
              <Alert className="app-empty-state app-empty-state--danger mb-3 text-center">
                Failed to load shots
              </Alert>
            ) : shotsData.length === 0 ? (
              <div className="text-center">
                <Alert className="app-empty-state mb-3">No shots available for this game.</Alert>
                <Button variant="warning" className="text-dark fw-semibold" onClick={() => setShowAddShotModal(true)}>
                  <FaPlus /> Add Shot
                </Button>
              </div>
            ) : (
              <>
                
                <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                  <Table striped bordered hover className="mb-0 app-table shot-detail-table">
                    <thead className="table-dark">
                      <tr>
                        {multiDeleteMode && (
                          <th style={stickyStyle}>
                            <Form.Check disabled />
                          </th>
                        )}

                        {Object.keys(shotDetailsColumns).map((key) => (
                          <th
                            key={key}
                            style={stickyStyle}
                            onClick={() => {
                              handleShotDetailSort(shotDetailsColumns[key]);
                            }}
                          >
                            {key}
                            <span
                              style={{
                                visibility:
                                  shotDetailsColumns[key] === shotDetailSortField
                                    ? "visible"
                                    : "hidden",
                              }}
                            >
                              {shotDetailSortOrder === 1 ? <FaChevronDown /> : <FaChevronUp />}
                            </span>
                          </th>
                        ))}

                        {!multiDeleteMode && (
                          <>
                            <th style={stickyStyle} />
                            <th style={stickyStyle} />
                          </>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {shotsData.map((s) => (
                        <tr key={s._id}>
                          {multiDeleteMode && (
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedShots.includes(s._id)}
                                onChange={(e) =>
                                  handleSelectShot(s._id, e.target.checked)
                                }
                              />
                            </td>
                          )}

                          <td>{s.shotType.replace(" attempt", "")}</td>
                          <td>{s.gameHalf}</td>
                          <td>{formatTime(deriveDisplayedGameTimeSeconds(s, selectedEntry))}</td>
                          <td>{s.teamShot}</td>
                          <td>{s.playerName}</td>
                          <td>{s.setPlay}</td>
                          <td>{s.shotPressure}</td>
                          <td>{s.shotOutcome}</td>
                          <td>{s.expectedScore.toFixed(2)}</td>

                          <td className="text-center">
                            {s.newAttack === "Yes" ? (
                              <FaCheck style={{ color: "limegreen" }} />
                            ) : (
                              <FaTimes style={{ color: "red" }} />
                            )}
                          </td>

                          {!multiDeleteMode && (
                            <>
                              <td>
                                <Button
                                  size="sm"
                                  variant="warning"
                                  className="text-dark app-table-edit-btn"
                                  onClick={() => {
                                    setSelectedShot(s);
                                    setShowEditShotModal(true);
                                  }}
                                >
                                  <FaEdit />
                                </Button>
                              </td>

                              <td>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => {
                                    setSelectedShotToDelete(s);
                                    setShowShotConfirm(true);
                                  }}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <Row className="d-flex justify-content-around align-items-center px-4">
                  <Col>
                    <Form.Label>
                      Showing {totalShots} Entries
                    </Form.Label>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        )}
      </Card>

          {/* ——— Game Details Panel (now summary tables) ——— */}
          <Card className="border-warning mt-4" ref={gameDetailsRef}>
            <Card.Header
              className="bg-warning text-dark d-flex justify-content-between align-items-center"
              onClick={() => setOpenGameDetails((o) => !o)}
              style={{ cursor: "pointer" }}
            >
              <strong>Game Details</strong>
              {openGameDetails ? <FaChevronUp /> : <FaChevronDown />}
            </Card.Header>
            {openGameDetails && selectedEntry && (
              <Card.Body className="bg-dark text-white">
                <h5 className="text-center mb-4">
                  {selectedEntry.HomeTeam} vs {selectedEntry.AwayTeam},{" "}
                  {new Date(selectedEntry.GameDate).toLocaleDateString()}
                </h5>
                <Form.Control
                  placeholder="Filter summary by type or outcome..."
                  className="mb-3"
                  value={detailSearch}
                  onChange={(e) => setDetailSearch(e.target.value)}
                />

                {[selectedEntry.HomeTeam, selectedEntry.AwayTeam].map(
                  (team) => {
                    const rows = (summaryByTeam[team] || [])
                      .filter(
                        (r) =>
                          r.shotType
                            .toLowerCase()
                            .includes(detailSearch.toLowerCase()) ||
                          r.shotOutcome
                            .toLowerCase()
                            .includes(detailSearch.toLowerCase()),
                      )
                      .sort((a, b) => {
                        let valA = a[summarySortKey];
                        let valB = b[summarySortKey];

                        if (summarySortKey === "num") {
                          return summarySortOrder === "asc"
                            ? valA - valB
                            : valB - valA;
                        }

                        return summarySortOrder === "asc"
                          ? String(valA).localeCompare(String(valB))
                          : String(valB).localeCompare(String(valA));
                      });

                    return (
                      <div key={team} className="mb-4">
                        <h6 className="text-warning">{team}</h6>
                        <Table striped bordered hover responsive className="app-table">
                          <thead className="table-success">
                            <tr>
                              <th
                                onClick={() => handleSummarySort("shotType")}
                                style={{
                                  cursor: "pointer",
                                }}
                              >
                                Shot Type{" "}
                                {summarySortKey === "shotType" &&
                                  (summarySortOrder === "asc" ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  ))}
                              </th>
                              <th
                                onClick={() => handleSummarySort("shotOutcome")}
                                style={{
                                  cursor: "pointer",
                                }}
                              >
                                Outcome{" "}
                                {summarySortKey === "shotOutcome" &&
                                  (summarySortOrder === "asc" ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  ))}
                              </th>
                              <th
                                onClick={() => handleSummarySort("num")}
                                style={{
                                  cursor: "pointer",
                                }}
                              >
                                Num{" "}
                                {summarySortKey === "num" &&
                                  (summarySortOrder === "asc" ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  ))}
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {rows.map((r) => (
                              <tr key={`${r.shotType}-${r.shotOutcome}`}>
                                <td>{r.shotType}</td>
                                <td>{r.shotOutcome}</td>
                                <td>{r.num}</td>
                              </tr>
                            ))}
                            {rows.length === 0 && (
                              <tr>
                                <td colSpan={3} className="text-center">
                                  No matching combos
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    );
                  },
                )}
              </Card.Body>
            )}
          </Card>


      {/* ——— Confirm Modals ——— */}
      <ConfirmDeletion
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={() => handleDeleteGame(selectedEntry)}
        message={`Delete ${selectedEntry?.HomeTeam} vs ${selectedEntry?.AwayTeam}?`}
      />
      <ConfirmDeletion
        show={showShotConfirm}
        onHide={() => setShowShotConfirm(false)}
        onConfirm={handleDeleteShot}
        message="Delete this shot?"
      />
      <ConfirmDeletion
        show={showMultiDeleteConfirm}
        onHide={() => setShowMultiDeleteConfirm(false)}
        onConfirm={handleDeleteSelected}
        message={`Delete ${selectedShots.length} selected shot(s)?`}
      />

      {/* ——— Add/Edit Modals ——— */}
      <AddGameModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        gameId={selectedEntry?._id}
        homeTeam={selectedEntry?.HomeTeam}
        awayTeam={selectedEntry?.AwayTeam}
      />
      <EditGameModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        onUpdate={handleUpdate}
        initialData={selectedEntry}
        gameId={selectedEntry?._id}
        homeTeam={selectedEntry?.HomeTeam}
        awayTeam={selectedEntry?.AwayTeam}
      />
      <AddShotModal
        show={showAddShotModal}
        onHide={() => setShowAddShotModal(false)}
        onAdd={handleAddShot}
        game={selectedEntry}
        previousSelectedHalf={previousSelectedHalf}
        setPreviousSelectedHalf={setPreviousSelectedHalf}
        previousShotTime={
          latestShotByGameTime != null
            ? formatTime(
                deriveEnteredVideoTimeSeconds(
                  latestShotByGameTime,
                  selectedEntry,
                ),
              )
            : "00:00:00"
        }
      />
      <EditShotModal
        show={showEditShotModal}
        onHide={() => setShowEditShotModal(false)}
        onUpdate={handleEditShot}
        initialData={selectedShot}
        game={selectedEntry}
      />
      {showTutorial && (
        <>
          {tutorialRect ? (
            <>
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: tutorialRect.top,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1040,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: 0,
                  width: tutorialRect.left,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1040,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: tutorialRect.left + tutorialRect.width,
                  width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1040,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top + tutorialRect.height,
                  left: 0,
                  width: "100vw",
                  height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1040,
                }}
              />
            </>
          ) : (
            <div
              onClick={closeTutorial}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(8, 12, 18, 0.72)",
                zIndex: 1040,
              }}
            />
          )}
          {tutorialRect && (
            <div
              style={{
                position: "fixed",
                top: tutorialRect.top,
                left: tutorialRect.left,
                width: tutorialRect.width,
                height: tutorialRect.height,
                border: "2px solid #f59e0b",
                borderRadius: 12,
                pointerEvents: "none",
                zIndex: 1041,
                transition:
                  "top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease",
              }}
            />
          )}
          {tutorialRect && (
            <Card
              className="border-warning"
              style={getTutorialCardStyle()}
            >
              <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                <strong>
                  {tutorialStepIndex + 1} / {tutorialSteps.length}{" "}
                  {tutorialSteps[tutorialStepIndex].title}
                </strong>
                <Button
                  variant="link"
                  className="text-dark p-0 text-decoration-none fw-semibold"
                  onClick={closeTutorial}
                >
                  Close
                </Button>
              </Card.Header>
              <Card.Body className="bg-dark text-white">
                <p className="mb-3">{tutorialSteps[tutorialStepIndex].body}</p>
                <div className="d-flex justify-content-between gap-2">
                  <Button
                    variant="outline-light"
                    size="sm"
                    disabled={tutorialStepIndex === 0}
                    onClick={prevTutorialStep}
                  >
                    Back
                  </Button>
                  <Button
                    variant="warning"
                    size="sm"
                    className="text-dark fw-semibold"
                    onClick={nextTutorialStep}
                  >
                    {tutorialStepIndex === tutorialSteps.length - 1 ? "Finish" : "Next"}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </Container>
    </>
  );
};

export default ShotDataScreen;
